import { createRef } from 'react';

import {
  eachDayOfInterval,
  startOfMonth,
  endOfMonth,
  eachMonthOfInterval,
  add,
} from 'date-fns';

import { MonthsToRender } from './types/MonthsToRender';
import { HourWithSelected, Interval } from './types/TimeSlot';

export const DEFAULT_MONTHS_COUNT = 12 as const;

export const provideMonthsToRender = (
  monthsCount = DEFAULT_MONTHS_COUNT,
  renderedIndex = 0,
): MonthsToRender => {
  const currentDate = new Date();

  const months = eachMonthOfInterval({
    start: currentDate,
    end: add(currentDate, { months: monthsCount }),
  });

  return months.map((month, index) => {
    return {
      index,
      month,
      days: eachDayOfInterval({
        start: index === 0 ? currentDate : startOfMonth(month),
        end: endOfMonth(month),
      }),
      isRendered: index === renderedIndex,
      isSelected: index === renderedIndex,
      ref: createRef(),
    };
  });
};

export const getSelectedMonth = (monthsToRender: MonthsToRender) =>
  monthsToRender.find((monthToRender) => monthToRender.isSelected)!;

export const getAllRenderedMonths = (monthsToRender: MonthsToRender) =>
  monthsToRender.filter((monthToRender) => monthToRender.isRendered);

export const updateMonthsToRender = (
  monthsToRender: MonthsToRender,
  targetMonthIndex: number,
  isSelectPrevious = false,
) => {
  return monthsToRender.map((monthToRender) => {
    const { index } = monthToRender;

    if (index === targetMonthIndex) {
      return { ...monthToRender, isSelected: true, isRendered: true };
    }

    if (isSelectPrevious && index === targetMonthIndex - 1) {
      return { ...monthToRender, isRendered: true };
    }

    return { ...monthToRender, isSelected: false };
  });
};

export const getHoursIntervals = (
  hoursWithSelected: HourWithSelected[],
): Interval[] => {
  const intervals: Interval[] = [];

  let i = 0;
  while (i < hoursWithSelected.length) {
    const { isSelected, timetable } = hoursWithSelected[i];
    const interval: HourWithSelected[] = [];

    while (
      i < hoursWithSelected.length &&
      hoursWithSelected[i].isSelected === isSelected
    ) {
      const currentHourWithSelected = hoursWithSelected[i];

      if (
        timetable?.timeSlotEnd &&
        currentHourWithSelected.hour > timetable.timeSlotEnd
      ) {
        break;
      }

      interval.push(currentHourWithSelected);
      i += 1;
    }

    intervals.push({ selected: isSelected, intervals: interval });
  }

  return intervals;
};
